import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantCreateStore } from "../../store";
import { EsignatureProvider } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputString } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { TenantTypeCreate } from "../../../tenantType/components/create/TenantTypeCreate";
import { TenantTypeCreateStore } from "../../../tenantType/store";
import { TenantStatusCreate } from "../../../tenantStatus/components/create/TenantStatusCreate";
import { TenantStatusCreateStore } from "../../../tenantStatus/store";
import { TenantPlanCreate } from "../../../tenantPlan/components/create/TenantPlanCreate";
import { TenantPlanCreateStore } from "../../../tenantPlan/store";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputDateTime } from "../../../common/controls";
import { LlmProviderCreate } from "../../../llmProvider/components/create/LlmProviderCreate";
import { LlmProviderCreateStore } from "../../../llmProvider/store";

export type TenantCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  eSignatureProviderError?: string;
  eSignatureProviderOptions: { id: string; displayId: string }[];
  eSignatureProvider: EsignatureProvider | "";
  onESignatureProviderChange?: (value: EsignatureProvider | "") => void;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  tenantTypeSelectedValue?: { displayId: string; id: string } | null;
  onTenantTypeSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onTenantTypeChange?: (value: string) => void;
  tenantTypeOptions?: { displayId: string; id: string }[];
  tenantTypeError?: string;
  statusSelectedValue?: { displayId: string; id: string } | null;
  onStatusSelected?: (value: { displayId: string; id: string } | null) => void;
  onStatusChange?: (value: string) => void;
  statusOptions?: { displayId: string; id: string }[];
  statusError?: string;
  planSelectedValue?: { displayId: string; id: string } | null;
  onPlanSelected?: (value: { displayId: string; id: string } | null) => void;
  onPlanChange?: (value: string) => void;
  planOptions?: { displayId: string; id: string }[];
  planError?: string;
  logoOnRestoreFile: (id: string) => void;
  logoOnLoadFile: (file: any) => void;
  logoOnCommitFile: (changes: GridChangeSet) => void;
  logoFiles: FileCRUDType[];
  logoError?: string;
  publicUrlSelectedValue?: { displayId: string; id: string } | null;
  onPublicUrlChange?: (value: string) => void;
  publicUrl: string;
  publicUrlError?: string;
  idERPSelectedValue?: { displayId: string; id: string } | null;
  onIdERPChange?: (value: string) => void;
  idERP: string;
  idERPError?: string;
  idCRMSelectedValue?: { displayId: string; id: string } | null;
  onIdCRMChange?: (value: string) => void;
  idCRM: string;
  idCRMError?: string;
  monthlyTokenLimitSelectedValue?: { displayId: string; id: string } | null;
  onMonthlyTokenLimitChange?: (value: number | undefined) => void;
  monthlyTokenLimit: number | undefined;
  monthlyTokenLimitError?: string;
  currentMonthTokensSelectedValue?: { displayId: string; id: string } | null;
  onCurrentMonthTokensChange?: (value: number) => void;
  currentMonthTokens: number;
  currentMonthTokensError?: string;
  tokenLimitResetDaySelectedValue?: { displayId: string; id: string } | null;
  onTokenLimitResetDayChange?: (value: number) => void;
  tokenLimitResetDay: number;
  tokenLimitResetDayError?: string;
  lastTokenResetDateSelectedValue?: { displayId: string; id: string } | null;
  onLastTokenResetDateChange?: (value: Date | null) => void;
  lastTokenResetDate: Date | null;
  lastTokenResetDateError?: string;
  ragLlmProviderSelectedValue?: { displayId: string; id: string } | null;
  onRagLlmProviderSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onRagLlmProviderChange?: (value: string) => void;
  ragLlmProviderOptions?: { displayId: string; id: string }[];
  ragLlmProviderError?: string;
  ragLlmModelSelectedValue?: { displayId: string; id: string } | null;
  onRagLlmModelChange?: (value: string) => void;
  ragLlmModel: string;
  ragLlmModelError?: string;
  currentStorageBytesSelectedValue?: { displayId: string; id: string } | null;
  onCurrentStorageBytesChange?: (value: number) => void;
  currentStorageBytes: number;
  currentStorageBytesError?: string;
  storageByteLimitSelectedValue?: { displayId: string; id: string } | null;
  onStorageByteLimitChange?: (value: number | undefined) => void;
  storageByteLimit: number | undefined;
  storageByteLimitError?: string;
  dummyCRUDRestoredObservable: number;
  tenantCreateStore: TenantCreateStore;
};

export const TenantCreateForm: React.FC<TenantCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    eSignatureProviderError,
    eSignatureProviderOptions = [],
    eSignatureProvider = "" as EsignatureProvider | "",
    onESignatureProviderChange = () => {},
    name = "",
    nameError,
    onNameChange = () => {},
    onTenantTypeSelected = () => {},
    tenantTypeSelectedValue = null,
    tenantTypeOptions = [],
    tenantTypeError,
    onTenantTypeChange = () => {},
    onStatusSelected = () => {},
    statusSelectedValue = null,
    statusOptions = [],
    statusError,
    onStatusChange = () => {},
    onPlanSelected = () => {},
    planSelectedValue = null,
    planOptions = [],
    planError,
    onPlanChange = () => {},
    logoOnRestoreFile,
    logoOnLoadFile,
    logoOnCommitFile,
    logoFiles,
    logoError,
    publicUrl = "",
    publicUrlError,
    onPublicUrlChange = () => {},
    idERP = "",
    idERPError,
    onIdERPChange = () => {},
    idCRM = "",
    idCRMError,
    onIdCRMChange = () => {},
    monthlyTokenLimit = undefined,
    monthlyTokenLimitError,
    onMonthlyTokenLimitChange = () => {},
    currentMonthTokens = 0,
    currentMonthTokensError,
    onCurrentMonthTokensChange = () => {},
    tokenLimitResetDay = 0,
    tokenLimitResetDayError,
    onTokenLimitResetDayChange = () => {},
    lastTokenResetDate = null,
    lastTokenResetDateError,
    onLastTokenResetDateChange = () => {},
    onRagLlmProviderSelected = () => {},
    ragLlmProviderSelectedValue = null,
    ragLlmProviderOptions = [],
    ragLlmProviderError,
    onRagLlmProviderChange = () => {},
    ragLlmModel = "",
    ragLlmModelError,
    onRagLlmModelChange = () => {},
    currentStorageBytes = 0,
    currentStorageBytesError,
    onCurrentStorageBytesChange = () => {},
    storageByteLimit = undefined,
    storageByteLimitError,
    onStorageByteLimitChange = () => {},
    dummyCRUDRestoredObservable,
    tenantCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="Tenant"
            mode="create"
            values={{}}
            options={{
              tenantType: tenantTypeOptions,
              status: statusOptions,
              plan: planOptions,
              ragLlmProvider: ragLlmProviderOptions,
            }}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "eSignatureProvider":
                  onESignatureProviderChange?.(valor);
                  break;
                case "name":
                  onNameChange?.(valor);
                  break;
                case "tenantType":
                  onTenantTypeSelected?.(valor);
                  break;
                case "status":
                  onStatusSelected?.(valor);
                  break;
                case "plan":
                  onPlanSelected?.(valor);
                  break;
                case "publicUrl":
                  onPublicUrlChange?.(valor);
                  break;
                case "idERP":
                  onIdERPChange?.(valor);
                  break;
                case "idCRM":
                  onIdCRMChange?.(valor);
                  break;
                case "monthlyTokenLimit":
                  onMonthlyTokenLimitChange?.(valor);
                  break;
                case "currentMonthTokens":
                  onCurrentMonthTokensChange?.(valor);
                  break;
                case "tokenLimitResetDay":
                  onTokenLimitResetDayChange?.(valor);
                  break;
                case "lastTokenResetDate":
                  onLastTokenResetDateChange?.(valor);
                  break;
                case "ragLlmProvider":
                  onRagLlmProviderSelected?.(valor);
                  break;
                case "ragLlmModel":
                  onRagLlmModelChange?.(valor);
                  break;
                case "currentStorageBytes":
                  onCurrentStorageBytesChange?.(valor);
                  break;
                case "storageByteLimit":
                  onStorageByteLimitChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="null"
            errorText={tenantCreateStore.getCardErrors("null")}
            title={langStore.groupTenantLabel}
            collapsible={true}
          >
            <FormControl fullWidth>
              <InputReference
                id="eSignatureProvider"
                autoFocus
                error={Boolean(eSignatureProviderError)}
                helperText={
                  eSignatureProviderError ||
                  tenantCreateStore.warnings["eSignatureProvider"]?.message
                }
                label={langStore.TenantESignatureProviderLabel}
                options={eSignatureProviderOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  eSignatureProvider
                    ? {
                        id: eSignatureProvider,
                        displayId:
                          eSignatureProviderOptions.find(
                            (o: { id: string }) => o.id === eSignatureProvider
                          )?.displayId || eSignatureProvider,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onESignatureProviderChange(
                    (value?.id as EsignatureProvider | "") ||
                      ("" as EsignatureProvider | "")
                  )
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="tenant"
            errorText={tenantCreateStore.getCardErrors("tenant")}
            title={langStore.groupTenantTenantLabel}
            collapsible={true}
            expanded={tenantCreateStore.getCardState("tenant")}
            toggleExpand={() => tenantCreateStore.toggleCardState("tenant")}
          >
            <FormControl fullWidth>
              <InputString
                value={name}
                error={Boolean(nameError)}
                warning={
                  !nameError && Boolean(tenantCreateStore.warnings["name"])
                }
                helperText={
                  nameError || tenantCreateStore.warnings["name"]?.message
                }
                label={langStore.TenantNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.TenantNameDescription}
              />
              <InputReference
                id="tenantType"
                label={langStore.TenantTenantTypeLabel}
                error={Boolean(tenantTypeError)}
                helperText={
                  tenantTypeError ||
                  tenantCreateStore.warnings["tenantType"]?.message
                }
                selectedValue={tenantTypeSelectedValue}
                onChange={(value: string) => onTenantTypeChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onTenantTypeSelected(value)
                }
                options={tenantTypeOptions}
                description={langStore.TenantTenantTypeDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantType_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantTypeCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantTypeCreate
                        tenantTypeCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantType"
                    cancelMethod="cancelNewTenantType"
                  />
                )}
              />
              <InputReference
                id="status"
                label={langStore.TenantStatusLabel}
                error={Boolean(statusError)}
                helperText={
                  statusError || tenantCreateStore.warnings["status"]?.message
                }
                selectedValue={statusSelectedValue}
                onChange={(value: string) => onStatusChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onStatusSelected(value)
                }
                options={statusOptions}
                description={langStore.TenantStatusDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantStatus_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantStatusCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantStatusCreate
                        tenantStatusCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantStatus"
                    cancelMethod="cancelNewTenantStatus"
                  />
                )}
              />
              <InputReference
                id="plan"
                label={langStore.TenantPlanLabel}
                error={Boolean(planError)}
                helperText={
                  planError || tenantCreateStore.warnings["plan"]?.message
                }
                selectedValue={planSelectedValue}
                onChange={(value: string) => onPlanChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onPlanSelected(value)
                }
                options={planOptions}
                description={langStore.TenantPlanDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantPlan_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantPlanCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantPlanCreate
                        tenantPlanCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantPlan"
                    cancelMethod="cancelNewTenantPlan"
                  />
                )}
              />
              <LoadFilesCRUDCompact
                rows={logoFiles}
                maxRows={1}
                showAddCommand={true}
                showDeleteCommand={true}
                label={langStore.TenantLogoLabel}
                description={langStore.TenantLogoDescription}
                showLabel={true}
                onLoadFile={(file: File) => logoOnLoadFile(file)}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
                onRestore={(id: string) => logoOnRestoreFile(id)}
                onCommitChanges={(changes: GridChangeSet) =>
                  logoOnCommitFile(changes)
                }
                dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                error={logoError}
              />

              <InputString
                value={publicUrl}
                error={Boolean(publicUrlError)}
                warning={
                  !publicUrlError &&
                  Boolean(tenantCreateStore.warnings["publicUrl"])
                }
                helperText={
                  publicUrlError ||
                  tenantCreateStore.warnings["publicUrl"]?.message
                }
                label={langStore.TenantPublicUrlLabel}
                onChange={(value: string) => onPublicUrlChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantPublicUrlDescription}
              />

              <InputString
                value={idERP}
                error={Boolean(idERPError)}
                warning={
                  !idERPError && Boolean(tenantCreateStore.warnings["idERP"])
                }
                helperText={
                  idERPError || tenantCreateStore.warnings["idERP"]?.message
                }
                label={langStore.TenantIdERPLabel}
                onChange={(value: string) => onIdERPChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantIdERPDescription}
              />

              <InputString
                value={idCRM}
                error={Boolean(idCRMError)}
                warning={
                  !idCRMError && Boolean(tenantCreateStore.warnings["idCRM"])
                }
                helperText={
                  idCRMError || tenantCreateStore.warnings["idCRM"]?.message
                }
                label={langStore.TenantIdCRMLabel}
                onChange={(value: string) => onIdCRMChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantIdCRMDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="llmLimits"
            errorText={tenantCreateStore.getCardErrors("llmLimits")}
            title={langStore.groupTenantLlmLimitsLabel}
            collapsible={true}
            expanded={tenantCreateStore.getCardState("llmLimits")}
            toggleExpand={() => tenantCreateStore.toggleCardState("llmLimits")}
          >
            <FormControl fullWidth>
              <InputNumber
                value={monthlyTokenLimit}
                nullable={true}
                error={Boolean(monthlyTokenLimitError)}
                warning={
                  !monthlyTokenLimitError &&
                  Boolean(tenantCreateStore.warnings["monthlyTokenLimit"])
                }
                helperText={
                  monthlyTokenLimitError ||
                  tenantCreateStore.warnings["monthlyTokenLimit"]?.message
                }
                allowNegative={false}
                label={langStore.TenantMonthlyTokenLimitLabel}
                decimalScale={0}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantMonthlyTokenLimitDescription}
                onChange={(value: number | undefined) =>
                  onMonthlyTokenLimitChange(value)
                }
              />
              <InputNumber
                value={currentMonthTokens}
                error={Boolean(currentMonthTokensError)}
                warning={
                  !currentMonthTokensError &&
                  Boolean(tenantCreateStore.warnings["currentMonthTokens"])
                }
                helperText={
                  currentMonthTokensError ||
                  tenantCreateStore.warnings["currentMonthTokens"]?.message
                }
                allowNegative={false}
                label={langStore.TenantCurrentMonthTokensLabel}
                decimalScale={0}
                description={langStore.TenantCurrentMonthTokensDescription}
                onChange={(value: number) => onCurrentMonthTokensChange(value)}
              />
              <InputNumber
                value={tokenLimitResetDay}
                error={Boolean(tokenLimitResetDayError)}
                warning={
                  !tokenLimitResetDayError &&
                  Boolean(tenantCreateStore.warnings["tokenLimitResetDay"])
                }
                helperText={
                  tokenLimitResetDayError ||
                  tenantCreateStore.warnings["tokenLimitResetDay"]?.message
                }
                allowNegative={false}
                label={langStore.TenantTokenLimitResetDayLabel}
                decimalScale={0}
                description={langStore.TenantTokenLimitResetDayDescription}
                onChange={(value: number) => onTokenLimitResetDayChange(value)}
              />
              <InputDateTime
                value={lastTokenResetDate}
                error={Boolean(lastTokenResetDateError)}
                warning={
                  !lastTokenResetDateError &&
                  Boolean(tenantCreateStore.warnings["lastTokenResetDate"])
                }
                helperText={
                  lastTokenResetDateError ||
                  tenantCreateStore.warnings["lastTokenResetDate"]?.message
                }
                label={langStore.TenantLastTokenResetDateLabel}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantLastTokenResetDateDescription}
                onChange={(value: Date | null | null) =>
                  onLastTokenResetDateChange(value)
                }
              />
              <InputReference
                id="ragLlmProvider"
                label={langStore.TenantRagLlmProviderLabel}
                error={Boolean(ragLlmProviderError)}
                helperText={
                  ragLlmProviderError ||
                  tenantCreateStore.warnings["ragLlmProvider"]?.message
                }
                selectedValue={ragLlmProviderSelectedValue}
                onChange={(value: string) => onRagLlmProviderChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onRagLlmProviderSelected(value)
                }
                options={ragLlmProviderOptions}
                description={langStore.TenantRagLlmProviderDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "llmProvider_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new LlmProviderCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <LlmProviderCreate
                        llmProviderCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewLlmProvider"
                    cancelMethod="cancelNewLlmProvider"
                  />
                )}
              />

              <InputString
                value={ragLlmModel}
                error={Boolean(ragLlmModelError)}
                warning={
                  !ragLlmModelError &&
                  Boolean(tenantCreateStore.warnings["ragLlmModel"])
                }
                helperText={
                  ragLlmModelError ||
                  tenantCreateStore.warnings["ragLlmModel"]?.message
                }
                label={langStore.TenantRagLlmModelLabel}
                onChange={(value: string) => onRagLlmModelChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantRagLlmModelDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="storageLimits"
            errorText={tenantCreateStore.getCardErrors("storageLimits")}
            title={langStore.groupTenantStorageLimitsLabel}
            collapsible={true}
            expanded={tenantCreateStore.getCardState("storageLimits")}
            toggleExpand={() =>
              tenantCreateStore.toggleCardState("storageLimits")
            }
          >
            <FormControl fullWidth>
              <InputNumber
                value={currentStorageBytes}
                error={Boolean(currentStorageBytesError)}
                warning={
                  !currentStorageBytesError &&
                  Boolean(tenantCreateStore.warnings["currentStorageBytes"])
                }
                helperText={
                  currentStorageBytesError ||
                  tenantCreateStore.warnings["currentStorageBytes"]?.message
                }
                allowNegative={false}
                label={langStore.TenantCurrentStorageBytesLabel}
                decimalScale={0}
                description={langStore.TenantCurrentStorageBytesDescription}
                onChange={(value: number) => onCurrentStorageBytesChange(value)}
              />
              <InputNumber
                value={storageByteLimit}
                nullable={true}
                error={Boolean(storageByteLimitError)}
                warning={
                  !storageByteLimitError &&
                  Boolean(tenantCreateStore.warnings["storageByteLimit"])
                }
                helperText={
                  storageByteLimitError ||
                  tenantCreateStore.warnings["storageByteLimit"]?.message
                }
                allowNegative={false}
                label={langStore.TenantStorageByteLimitLabel}
                decimalScale={0}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantStorageByteLimitDescription}
                onChange={(value: number | undefined) =>
                  onStorageByteLimitChange(value)
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
